import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faPlus, 
  faSearch, 
  faUsers, 
  faUserPlus,
  faEye,
  faEyeSlash,
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faCheck,
  faTimes,
  faEdit,
  faSync,
  faIdCard,
  faBuilding,
  faChartLine,
  faShieldAlt,
  faClock,
  faMapMarkerAlt,
  faFilter,
  faDownload,
  faTrash,
  faStar,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import config from '../config/config';
import GoogleMapsLocationPicker from './GoogleMapsLocationPicker';
import UserStatusSyncPanel, { describeSyncResult, emptySyncCounts } from './UserStatusSyncPanel';
import './UserManagementPage.css';

// PinLevel enum names on the backend, in rank order
const PIN_LEVEL_OPTIONS = [
  ['PERCENT_3', '3%'], ['PERCENT_6', '6%'], ['PERCENT_9', '9%'], ['PERCENT_12', '12%'],
  ['PERCENT_15', '15%'], ['PERCENT_18', '18%'], ['PERCENT_21', '21%'], ['PERCENT_23', '23%'],
  ['PERCENT_25', '25%'], ['SILVER_SPONSOR', 'Silver Sponsor'], ['SILVER', 'Silver'],
  ['GOLD_SPONSOR', 'Gold Sponsor'], ['GOLD', 'Gold'], ['PLATINUM', 'Platinum'], ['RUBY', 'Ruby'],
  ['SAPPHIRE', 'Sapphire'], ['EMERALD', 'Emerald'], ['DIAMOND', 'Diamond'],
  ['CROWN_AMBASSADOR', 'Crown Ambassador'], ['UNKNOWN', 'Unknown']
];

const YES_NO = [['true', 'Yes'], ['false', 'No']];
const VERIFIED_UNVERIFIED = [['true', 'Verified'], ['false', 'Unverified']];

// Select filters on the View Users tab; keys match the /um/v1/admin/user/list query params
const FILTER_DEFS = [
  { key: 'active', label: 'Status', options: [['true', 'Active'], ['false', 'Inactive']] },
  { key: 'emailVerified', label: 'Email', options: VERIFIED_UNVERIFIED },
  { key: 'phoneVerified', label: 'Phone', options: VERIFIED_UNVERIFIED },
  { key: 'firstContractYear', label: 'First contract year', options: YES_NO },
  { key: 'registered', label: 'Registered with Amway', options: YES_NO },
  { key: 'pinLevel', label: 'Pin level', options: PIN_LEVEL_OPTIONS },
  { key: 'missingUpline', label: 'Upline', options: [['true', 'Missing'], ['false', 'Assigned']] },
  { key: 'uplineOverride', label: 'Upline override', options: YES_NO },
  { key: 'hasAddress', label: 'Address', options: [['true', 'Has address'], ['false', 'No address']] }
];

const EMPTY_FILTERS = {
  team: '',
  active: '',
  emailVerified: '',
  phoneVerified: '',
  firstContractYear: '',
  registered: '',
  pinLevel: '',
  missingUpline: '',
  uplineOverride: '',
  hasAddress: '',
  createdFrom: '',
  createdTo: ''
};

const SUMMARY_CARDS = [
  { key: 'total', label: 'Matching users' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'emailVerified', label: 'Email verified' },
  { key: 'phoneVerified', label: 'Phone verified' },
  { key: 'firstContractYear', label: 'First contract year' },
  { key: 'registered', label: 'Registered with Amway' },
  { key: 'missingUpline', label: 'Missing upline' },
  { key: 'uplineOverride', label: 'Upline override' },
  { key: 'withAddress', label: 'With address' }
];

// Users per /list/sync-status call: each is one Amway call, so keep a batch well inside request timeouts
const SYNC_BATCH_SIZE = 25;

// Local midnight of a YYYY-MM-DD date, plus dayOffset days, as an ISO instant
const toStartOfDayIso = (dateString, dayOffset = 0) => {
  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + dayOffset);
  return date.toISOString();
};

// Copies a Google place (from /em/v1/places/details, via GoogleMapsLocationPicker) onto the address
// form. Only fields the place has are replaced, so a typed street line 2 or label survives.
export const applyPlaceToAddress = (form, place) => {
  const street = place.address || (place.formattedAddress || '').split(',')[0].trim();
  return {
    ...form,
    streetLine1: street || form.streetLine1,
    city: place.city || form.city,
    state: place.state || form.state,
    zipCode: place.postalCode || form.zipCode,
    country: place.countryCode || place.country || form.country
  };
};

const UserManagementPage = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();

  // State for active section
  const [activeSection, setActiveSection] = useState('view'); // 'view', 'create', or 'upline'

  // State for user list
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // State for upline management
  const [nullUplineUsers, setNullUplineUsers] = useState([]);
  const [nullUplinePagination, setNullUplinePagination] = useState({
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });
  const [selectedUserForUpline, setSelectedUserForUpline] = useState(null);
  const [uplinePlatinumIboValue, setUplinePlatinumIboValue] = useState('');
  const [updatingUpline, setUpdatingUpline] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });

  // Search and filter state
  const [searchInput, setSearchInput] = useState(''); // What's typed; searchTerm follows it after a pause
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(true);

  // Summary counts for the current search and filters
  const [summary, setSummary] = useState(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  // Latest request ids, so a slow older response can't overwrite a newer one
  const usersRequestRef = useRef(0);
  const summaryRequestRef = useRef(0);

  // Create user form state
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    email: '',
    iboNumber: '',
    teamName: '',
    phoneNumber: '',
    sendInEnglish: false
  });
  const [creatingUser, setCreatingUser] = useState(false);

  // View user details state
  const [selectedUserForView, setSelectedUserForView] = useState(null);

  // Edit User modal's address draft: staged here, saved with Update User. Entries carry a local key;
  // id is null for a new address, and removed marks an existing one to delete on save
  const [addressDraft, setAddressDraft] = useState([]);
  // Add/edit sub-form: null when closed, key null for a new address
  const [addressForm, setAddressForm] = useState(null);
  const [addressFormError, setAddressFormError] = useState(null);
  const newAddressKeyRef = useRef(0);
  
  // Edit user state
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    email: '',
    phone: '',
    iboName: '',
    uplinePlatinumIbo: '',
    teamName: '',
    emailVerified: false,
    phoneVerified: false,
    userVerified: false,
    isFirstContractYear: false
  });
  const [updatingUser, setUpdatingUser] = useState(false);
  const [editModalError, setEditModalError] = useState(null);
  
  // Refresh KPI state
  const [refreshingKpi, setRefreshingKpi] = useState(null); // Stores IBO number currently being refreshed

  // Amway status sync: one user (IBO number being synced), or every user matching the filters
  const [syncingUser, setSyncingUser] = useState(null);
  const [syncRun, setSyncRun] = useState(null);
  const stopSyncRef = useRef(false);

  // Leaving the page stops a filtered sync after its current batch
  useEffect(() => () => { stopSyncRef.current = true; }, []);

  // Load users when component mounts or when pagination/search/filters change
  useEffect(() => {
    if (activeSection === 'view') {
      fetchUsers();
    }
  }, [activeSection, pagination.currentPage, sortBy, sortDir, searchTerm, filters]);

  // Summary only depends on what's matched, not on the page or sort
  useEffect(() => {
    if (activeSection === 'view') {
      fetchSummary();
    }
  }, [activeSection, searchTerm, filters]);

  // Apply the search box 300ms after typing stops instead of on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchInput.trim();
      if (term !== searchTerm) {
        setSearchTerm(term);
        setPagination(prev => ({ ...prev, currentPage: 0 })); // Reset to first page
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load teams for the create form, and once for the View Users team filter
  useEffect(() => {
    if (activeSection === 'create' || (activeSection === 'view' && teams.length === 0)) {
      fetchTeams();
    }
  }, [activeSection]);

  // Load users with null upline when upline section is active
  useEffect(() => {
    if (activeSection === 'upline') {
      fetchNullUplineUsers();
    }
  }, [activeSection, nullUplinePagination.currentPage]);

  // Search term and filters as query params, shared by the list and its summary
  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return;
      if (key === 'createdFrom') {
        params.append(key, toStartOfDayIso(value));
      } else if (key === 'createdTo') {
        // Inclusive end date: everything before the next local midnight
        params.append(key, toStartOfDayIso(value, 1));
      } else {
        params.append(key, value);
      }
    });
    return params;
  };

  const fetchUsers = async () => {
    const requestId = ++usersRequestRef.current;
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const queryParams = buildFilterParams();
      queryParams.append('page', pagination.currentPage);
      queryParams.append('size', pagination.pageSize);
      queryParams.append('sortBy', sortBy);
      queryParams.append('sortDir', sortDir);

      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/user/list?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (requestId !== usersRequestRef.current) return;

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch users');
      }

      const data = await response.json();
      if (requestId !== usersRequestRef.current) return;

      if (data.status === 'success') {
        setUsers(data.data.users || []);
        setPagination(prev => ({
          ...prev,
          ...data.data.pagination,
          sortBy: sortBy,
          sortDirection: sortDir
        }));
      } else {
        throw new Error(data.message || 'Failed to load users');
      }

    } catch (err) {
      if (requestId !== usersRequestRef.current) return;
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      if (requestId === usersRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchSummary = async () => {
    const requestId = ++summaryRequestRef.current;
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/list/summary?${buildFilterParams().toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      if (requestId !== summaryRequestRef.current) return;

      // A bad filter already surfaces as the list's error message
      setSummary(response.ok && data.status === 'success' ? data.data : null);
    } catch (err) {
      if (requestId !== summaryRequestRef.current) return;
      console.error('Error fetching user summary:', err);
      setSummary(null);
    }
  };

  // Every user matching the current search and filters (not just this page), in the table's order
  const handleDownloadCsv = async () => {
    setDownloadingCsv(true);
    setError(null);
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const queryParams = buildFilterParams();
      queryParams.append('sortBy', sortBy);
      queryParams.append('sortDir', sortDir);

      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/list/export?${queryParams.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to download users');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading users CSV:', err);
      setError(err.message || 'Failed to download users');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const fetchTeams = async () => {
    try {
      setLoadingTeams(true);
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setTeams(result.data || []);
          // Auto-select first team if available
          if (result.data.length > 0 && !createUserForm.teamName) {
            setCreateUserForm(prev => ({
              ...prev,
              teamName: result.data[0].name
            }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams. Please try again.');
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchNullUplineUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const queryParams = new URLSearchParams({
        page: nullUplinePagination.currentPage.toString(),
        size: nullUplinePagination.pageSize.toString(),
        sortBy: nullUplinePagination.sortBy,
        sortDir: nullUplinePagination.sortDirection
      });

      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/null-upline-platinum?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch users with null upline');
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setNullUplineUsers(data.data.users || []);
        setNullUplinePagination(prev => ({
          ...prev,
          ...data.data.pagination
        }));
      } else {
        throw new Error(data.message || 'Failed to load users with null upline');
      }

    } catch (err) {
      console.error('Error fetching users with null upline:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUplinePlatinumIbo = async () => {
    if (!selectedUserForUpline || !uplinePlatinumIboValue.trim()) {
      setError('Please enter an upline platinum IBO number');
      return;
    }

    try {
      setUpdatingUpline(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/update-upline-platinum`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId: selectedUserForUpline.id,
            uplinePlatinumIbo: uplinePlatinumIboValue.trim()
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update upline platinum IBO');
      }

      if (data.status === 'success') {
        setSuccessMessage('Upline platinum IBO updated successfully!');
        
        // Clear selection and input
        setSelectedUserForUpline(null);
        setUplinePlatinumIboValue('');
        
        // Refresh the list
        await fetchNullUplineUsers();
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error(data.message || 'Failed to update upline platinum IBO');
      }

    } catch (err) {
      console.error('Error updating upline platinum IBO:', err);
      setError(err.message);
    } finally {
      setUpdatingUpline(false);
    }
  };

  const handleSearch = (e) => {
    setSearchInput(e.target.value);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 0 })); // Reset to first page
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, currentPage: 0 }));
  };

  // One chip per filter that's set, labelled the same way as its dropdown
  const getActiveFilterChips = () => {
    const chips = [];
    if (filters.team) {
      chips.push({ key: 'team', text: `Team: ${filters.team}` });
    }
    FILTER_DEFS.forEach(({ key, label, options }) => {
      const option = options.find(([value]) => value === filters[key]);
      if (option) {
        chips.push({ key, text: `${label}: ${option[1]}` });
      }
    });
    if (filters.createdFrom) {
      chips.push({ key: 'createdFrom', text: `Created from: ${filters.createdFrom}` });
    }
    if (filters.createdTo) {
      chips.push({ key: 'createdTo', text: `Created to: ${filters.createdTo}` });
    }
    return chips;
  };

  const handleSort = (field) => {
    const newSortDir = sortBy === field && sortDir === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortDir(newSortDir);
    setPagination(prev => ({ ...prev, currentPage: 0 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleNullUplinePageChange = (newPage) => {
    setNullUplinePagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleCreateUserInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreateUserForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      setCreatingUser(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/user/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(createUserForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create user');
      }

      if (data.status === 'success') {
        setSuccessMessage(`User created successfully! ${data.data.emailSent ? 'Credentials sent via email.' : ''}`);
        
        // Reset form
        setCreateUserForm({
          email: '',
          iboNumber: '',
          teamName: teams.length > 0 ? teams[0].name : '',
          phoneNumber: '',
          sendInEnglish: false
        });

        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
        
        // If we're on view section, refresh the user list
        if (activeSection === 'view') {
          fetchUsers();
          fetchSummary();
        }
      } else {
        throw new Error(data.message || 'Failed to create user');
      }

    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUserForView(user);
  };

  // Every opening of Edit User starts a fresh draft from the user's saved addresses, so a cancelled
  // edit never carries over
  const startAddressDraft = (user) => {
    setAddressDraft((user?.addresses || []).map(address => ({
      key: `saved-${address.id}`,
      id: address.id,
      label: address.label || '',
      streetLine1: address.streetLine1 || '',
      streetLine2: address.streetLine2 || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zipCode || '',
      country: address.country || 'US',
      isDefault: !!address.isDefault,
      removed: false
    })));
    setAddressForm(null);
    setAddressFormError(null);
  };

  const ADDRESS_FIELDS = ['label', 'streetLine1', 'streetLine2', 'city', 'state', 'zipCode', 'country'];

  // The draft as the backend will store it, for comparing with what is saved
  const addressSignature = (addresses) => JSON.stringify(addresses.map(a => [
    a.id ?? null, ...ADDRESS_FIELDS.map(f => (a[f] || '').trim()), !!a.isDefault
  ]));

  const activeAddressDraft = addressDraft.filter(a => !a.removed);
  const addressesChanged = !!selectedUserForEdit && addressSignature(activeAddressDraft) !==
    addressSignature((selectedUserForEdit.addresses || []).map(a => ({ ...a, country: a.country || 'US' })));

  // Exactly one primary among the addresses that stay, as the backend keeps it
  const withOnePrimary = (draft, preferredKey) => {
    const active = draft.filter(a => !a.removed);
    const primaryKey = preferredKey
      ?? active.find(a => a.isDefault)?.key
      ?? active[0]?.key;
    return draft.map(a => ({ ...a, isDefault: !a.removed && a.key === primaryKey }));
  };

  const openAddressForm = (entry) => {
    setAddressFormError(null);
    setAddressForm(entry
      ? { ...entry, makePrimary: false }
      : {
          key: null, id: null, label: '', streetLine1: '', streetLine2: '', city: '',
          state: '', zipCode: '', country: 'US', makePrimary: false
        });
  };

  const handleAddressFieldChange = (field, value) => {
    setAddressForm(prev => ({ ...prev, [field]: value }));
  };

  // A place picked on the Google map fills the address fields (the same picker Event Management uses
  // for venues). Street line 2 and the label are left alone, and every field stays editable.
  const handleAddressPlaceSelect = (place) => {
    if (!place) return;
    setAddressForm(prev => applyPlaceToAddress(prev, place));
    setAddressFormError(null);
  };

  const applyAddressForm = () => {
    const missing = [['streetLine1', 'Street line 1'], ['city', 'City'], ['state', 'State'], ['zipCode', 'ZIP']]
      .filter(([field]) => !(addressForm[field] || '').trim())
      .map(([, label]) => label);
    if (missing.length > 0) {
      setAddressFormError(`${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required`);
      return;
    }

    const { makePrimary, ...entry } = addressForm;
    setAddressDraft(prev => {
      if (entry.key) {
        const next = prev.map(a => (a.key === entry.key ? { ...a, ...entry } : a));
        return withOnePrimary(next, makePrimary ? entry.key : undefined);
      }
      const key = `new-${++newAddressKeyRef.current}`;
      return withOnePrimary([...prev, { ...entry, key, isDefault: false, removed: false }],
        makePrimary ? key : undefined);
    });
    setAddressForm(null);
    setAddressFormError(null);
  };

  const handleMakePrimaryDraft = (key) => {
    setAddressDraft(prev => withOnePrimary(prev, key));
  };

  // A new address just disappears; a saved one stays listed, struck through, until Update User
  const handleRemoveDraft = (key) => {
    setAddressForm(prev => (prev?.key === key ? null : prev));
    setAddressDraft(prev => withOnePrimary(prev
      .filter(a => a.key !== key || a.id !== null)
      .map(a => (a.key === key ? { ...a, removed: true, isDefault: false } : a))));
  };

  const handleUndoRemoveDraft = (key) => {
    setAddressDraft(prev => withOnePrimary(prev.map(a => (a.key === key ? { ...a, removed: false } : a))));
  };

  // Saves the whole draft in one all-or-nothing request; throws with the backend's reason on refusal
  const saveAddressDraft = async (token, userId) => {
    const response = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/users/${userId}/addresses`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        addresses: activeAddressDraft.map(a => ({
          id: a.id,
          ...Object.fromEntries(ADDRESS_FIELDS.map(f => [f, (a[f] || '').trim() || null])),
          isDefault: a.isDefault
        }))
      })
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      // Validation errors list the bad fields in details
      throw new Error(`Addresses not saved: ${(data && (data.details || data.message)) || 'request failed'}`);
    }
  };

  const handleEditUser = (user) => {
    // Close view modal if open
    setSelectedUserForView(null);

    setSelectedUserForEdit(user);
    startAddressDraft(user);
    setEditUserForm({
      email: user.email || '',
      phone: user.phone || '',
      iboName: user.iboName || '',
      uplinePlatinumIbo: user.uplinePlatinumIbo || '',
      teamName: user.teamName || '',
      emailVerified: user.emailVerified || false,
      phoneVerified: user.phoneVerified || false,
      userVerified: user.userVerified || false,
      isFirstContractYear: user.isFirstContractYear || false
    });
    setError(null);
    setEditModalError(null);
    
    // Fetch teams if not already loaded
    if (teams.length === 0) {
      fetchTeams();
    }
  };

  const handleEditUserFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditUserForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    // An address still open in the sub-form isn't in the draft yet, so saving now would drop it
    if (addressForm) {
      setEditModalError('Finish the address you are editing (Add to list / Apply changes) or cancel it first.');
      return;
    }

    try {
      setUpdatingUser(true);
      setEditModalError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      // Addresses first: their save is all-or-nothing, and the one most likely refused (an address a
      // subscription ships to), so a refusal leaves both the addresses and the user unchanged
      if (addressesChanged) {
        await saveAddressDraft(token, selectedUserForEdit.id);
      }

      // Prepare update data - only include changed fields
      const updateData = {
        userId: selectedUserForEdit.id,
        ...editUserForm
      };

      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/user/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update user');
      }

      if (data.status === 'success') {
        setSuccessMessage('User updated successfully!');
        
        // Close edit form
        setSelectedUserForEdit(null);
        setEditModalError(null);
        setEditUserForm({
          email: '',
          phone: '',
          iboName: '',
          uplinePlatinumIbo: '',
          teamName: '',
          emailVerified: false,
          phoneVerified: false,
          userVerified: false,
          isFirstContractYear: false
        });

        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
        
        // Refresh the user list
        if (activeSection === 'view') {
          fetchUsers();
          fetchSummary();
        } else if (activeSection === 'upline') {
          fetchNullUplineUsers();
        }
      } else {
        throw new Error(data.message || 'Failed to update user');
      }

    } catch (err) {
      console.error('Error updating user:', err);
      setEditModalError(err.message);
    } finally {
      setUpdatingUser(false);
    }
  };

  const handleRefreshKpi = async (iboNumber) => {
    try {
      setRefreshingKpi(iboNumber);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/refresh-kpi?iboNumber=${encodeURIComponent(iboNumber)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to refresh KPI data');
      }

      if (data.status === 'success') {
        setSuccessMessage('KPI data refreshed successfully!');
        
        // Refresh the user list to show updated data
        if (activeSection === 'view') {
          fetchUsers();
          fetchSummary();
        } else if (activeSection === 'upline') {
          fetchNullUplineUsers();
        }
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error(data.message || 'Failed to refresh KPI data');
      }

    } catch (err) {
      console.error('Error refreshing KPI data:', err);
      setError(err.message);
    } finally {
      setRefreshingKpi(null);
    }
  };

  // Re-checks one user's first contract year and Amway registration with Amway's /validate API
  const handleSyncUser = async (iboNumber) => {
    try {
      setSyncingUser(iboNumber);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/sync-status?iboNumber=${encodeURIComponent(iboNumber)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status !== 'success') {
        throw new Error(data.message || 'Failed to sync user status');
      }

      const result = data.data;
      setSuccessMessage(describeSyncResult(result));
      setTimeout(() => setSuccessMessage(''), 5000);
      setSelectedUserForView(prev => (prev && prev.iboNumber === result.iboNumber
        ? { ...prev, isFirstContractYear: result.firstContractYear }
        : prev));
      if (result.outcome !== 'UNCHANGED' && result.outcome !== 'SKIPPED_NO_CONSENT') {
        fetchUsers();
        fetchSummary();
      }
    } catch (err) {
      console.error('Error syncing user status:', err);
      setError(err.message);
    } finally {
      setSyncingUser(null);
    }
  };

  // Syncs every user matching the current search and filters, a batch at a time, in id order.
  // The filters are fixed when it starts; changing them meanwhile doesn't change what's synced.
  const handleSyncFiltered = async () => {
    const total = summary?.total || 0;
    if (total === 0 || syncRun?.running) return;

    const chips = getActiveFilterChips().map(chip => chip.text);
    if (searchTerm) chips.unshift(`Search: ${searchTerm}`);
    const filterText = chips.length > 0 ? `Users matching ${chips.join(', ')}` : 'All users';
    const minutes = Math.max(1, Math.ceil((total * 0.5) / 60));
    if (!window.confirm(
      `Sync first contract year and Amway registration for ${total.toLocaleString()} users?\n\n` +
      `${filterText}.\n\nThis checks each user with Amway and takes about ${minutes} minute(s). ` +
      'You can stop it at any time.'
    )) {
      return;
    }

    const params = buildFilterParams();
    stopSyncRef.current = false;
    let run = {
      total,
      processed: 0,
      counts: emptySyncCounts(),
      results: [],
      running: true,
      stopping: false,
      stopped: false,
      error: null,
      filterText
    };
    setSyncRun(run);
    setError(null);

    let afterId = 0;
    try {
      while (true) {
        const token = await getAccessToken();
        if (!token) {
          throw new Error('Authentication token not available');
        }
        const query = new URLSearchParams(params);
        query.set('afterId', afterId);
        query.set('size', SYNC_BATCH_SIZE);

        const response = await fetch(
          `${config.API_BASE_URL}/um/v1/admin/user/list/sync-status?${query.toString()}`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || 'Failed to sync user status');
        }

        const batch = data.data;
        const counts = { ...run.counts };
        Object.entries(batch.counts || {}).forEach(([key, value]) => {
          counts[key] = (counts[key] || 0) + value;
        });
        const processed = run.processed + batch.processed;
        run = {
          ...run,
          processed,
          // Users can join the filtered set while it runs, so the total may grow
          total: Math.max(run.total, processed),
          counts,
          results: [...run.results, ...(batch.results || [])]
        };

        if (!batch.hasMore || batch.lastId == null) break;
        if (stopSyncRef.current) {
          run = { ...run, stopped: true };
          break;
        }
        afterId = batch.lastId;
        const progress = run;
        setSyncRun(prev => ({ ...progress, stopping: prev?.stopping || false }));
      }
    } catch (err) {
      console.error('Error syncing filtered user status:', err);
      run = { ...run, error: err.message };
    }

    setSyncRun({ ...run, running: false, stopping: false });
    fetchUsers();
    fetchSummary();
  };

  const handleStopSync = () => {
    stopSyncRef.current = true;
    setSyncRun(prev => (prev ? { ...prev, stopping: true } : prev));
  };

  const handleBack = () => {
    navigate('/');
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return faSort;
    return sortDir === 'asc' ? faSortUp : faSortDown;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      
      // Check if timestamp is a Unix timestamp (number) or array format
      if (typeof timestamp === 'number') {
        // Unix timestamp in seconds, convert to milliseconds
        date = new Date(timestamp * 1000);
      } else if (Array.isArray(timestamp)) {
        // Array format: [year, month, day, hour, minute, second]
        const [year, month, day, hour, minute, second] = timestamp;
        date = new Date(year, month - 1, day, hour, minute, second);
      } else {
        // Try parsing as ISO string or other format
        date = new Date(timestamp);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) return 'N/A';
      
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'N/A';
    }
  };

  const isKpiInvalid = (metadata) => {
    // KPI is invalid if:
    // 1. metadata is null/undefined
    // 2. metadata.message is not null (indicates an error)
    // 3. metadata.message contains error indicators like "not associated with AP" or "Inactive IBO"
    return !metadata || (metadata.message && (
      metadata.message.includes('not associated with AP') || 
      metadata.message.includes('Inactive IBO')
    ));
  };

  const getKpiStatus = (metadata) => {
    if (!metadata) {
      return { status: 'unknown', text: 'No Data', color: '#999' };
    }
    // Check for error messages indicating invalid/inactive IBO
    if (metadata.message) {
      if (metadata.message.includes('not associated with AP')) {
        return { status: 'invalid', text: 'Invalid IBO', color: '#e53935' };
      }
      if (metadata.message.includes('Inactive IBO')) {
        return { status: 'invalid', text: 'Inactive IBO', color: '#e53935' };
      }
    }
    if (metadata.current_award && metadata.current_award.trim()) {
      return { status: 'valid', text: metadata.current_award, color: '#4CAF50' };
    }
    // If we have valid metadata but no current_award (or empty), show 0 Percent in green
    return { status: 'valid', text: '0 Percent', color: '#4CAF50' };
  };

  // Addresses section inside the Edit User form. Nothing here saves until Update User, so the
  // sub-form is a div (forms can't nest) and Enter applies the address instead of submitting
  const renderAddressEditor = () => {
    const editingPrimary = addressForm?.key && addressDraft.find(a => a.key === addressForm.key)?.isDefault;
    const onAddressKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyAddressForm();
      }
    };
    const field = (name, label, required = false, placeholder) => (
      <label>
        <span>{label}{required ? ' *' : ''}</span>
        <input
          type="text"
          value={addressForm[name]}
          onChange={(e) => handleAddressFieldChange(name, e.target.value)}
          onKeyDown={onAddressKeyDown}
          placeholder={placeholder}
          disabled={updatingUser}
        />
      </label>
    );

    return (
      <div className="edit-addresses">
        <div className="form-section-title">Addresses</div>
        <p className="edit-addresses-note">
          {addressesChanged
            ? 'Address changes are not saved yet. Click Update User to save them, or Cancel to discard.'
            : 'Changes here are saved when you click Update User.'}
        </p>

        {addressDraft.length === 0 && <p className="edit-addresses-empty">No address on file</p>}

        {addressDraft.map((address, index) => {
          const name = address.label || `Address ${index + 1}`;
          return (
            <div key={address.key} className={`edit-address-item ${address.removed ? 'removed' : ''}`}>
              <div className="edit-address-text">
                <span className="address-label">
                  {name}
                  {address.isDefault && <span className="status-badge verified">Default</span>}
                  {address.id === null && <span className="status-badge first-year">New</span>}
                  {address.removed && <span className="status-badge unverified">Will be deleted</span>}
                </span>
                <span>{[address.streetLine1, address.streetLine2].filter(Boolean).join(', ')}</span>
                <span>
                  {[address.city, [address.state, address.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                  {address.country ? `, ${address.country}` : ''}
                </span>
              </div>

              <div className="address-actions">
                {address.removed ? (
                  <button type="button" onClick={() => handleUndoRemoveDraft(address.key)} disabled={updatingUser}>
                    Undo
                  </button>
                ) : (
                  <>
                    <button type="button" onClick={() => openAddressForm(address)} disabled={updatingUser}>
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </button>
                    {!address.isDefault && (
                      <button type="button" onClick={() => handleMakePrimaryDraft(address.key)} disabled={updatingUser}>
                        <FontAwesomeIcon icon={faStar} /> Make primary
                      </button>
                    )}
                    <button
                      type="button"
                      className="danger"
                      onClick={() => handleRemoveDraft(address.key)}
                      disabled={updatingUser}
                    >
                      <FontAwesomeIcon icon={faTrash} /> {address.id === null ? 'Remove' : 'Delete'}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}

        {addressForm ? (
          <div className="address-form">
            <h5>{addressForm.key ? 'Edit address' : 'New address'}</h5>
            {/* Enter picks a Google suggestion; it must not also submit Update User */}
            <div
              className="address-place-search"
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
            >
              <span>Find the address on Google Maps, then check the fields below</span>
              <GoogleMapsLocationPicker
                key={addressForm.key || 'new-address'}
                onLocationSelect={handleAddressPlaceSelect}
                placeholder="Search for an address or click on the map..."
              />
            </div>
            {addressFormError && <div className="address-error">{addressFormError}</div>}
            {field('label', 'Label', false, 'Home, Office...')}
            {field('streetLine1', 'Street line 1', true)}
            {field('streetLine2', 'Street line 2')}
            <div className="address-form-row">
              {field('city', 'City', true)}
              {field('state', 'State', true)}
            </div>
            <div className="address-form-row">
              {field('zipCode', 'ZIP', true)}
              {field('country', 'Country')}
            </div>
            {activeAddressDraft.length > 0 && !editingPrimary && (
              <label className="address-form-checkbox">
                <input
                  type="checkbox"
                  checked={addressForm.makePrimary}
                  onChange={(e) => handleAddressFieldChange('makePrimary', e.target.checked)}
                  disabled={updatingUser}
                />
                <span>Make this the primary address</span>
              </label>
            )}
            <div className="address-form-actions">
              <button type="button" className="primary" onClick={applyAddressForm} disabled={updatingUser}>
                {addressForm.key ? 'Apply changes' : 'Add to list'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddressForm(null);
                  setAddressFormError(null);
                }}
                disabled={updatingUser}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="add-address-button"
            onClick={() => openAddressForm(null)}
            disabled={updatingUser}
          >
            <FontAwesomeIcon icon={faPlus} /> Add address
          </button>
        )}
      </div>
    );
  };

  const activeFilterChips = getActiveFilterChips();

  const renderViewUsers = () => (
    <div className="user-list-section">
      <div className="section-header">
        <h3>
          <FontAwesomeIcon icon={faUsers} />
          View Users
        </h3>
        <div className="search-container">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search users by email, IBO number, name, phone, or team..."
              value={searchInput}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
        </div>
        <button
          type="button"
          className={`filter-toggle-button ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(prev => !prev)}
        >
          <FontAwesomeIcon icon={faFilter} />
          Filters
          {activeFilterChips.length > 0 && (
            <span className="filter-count">{activeFilterChips.length}</span>
          )}
        </button>
        <button
          type="button"
          className="filter-toggle-button download-csv-button"
          onClick={handleDownloadCsv}
          disabled={downloadingCsv || summary?.total === 0}
          title="Download every user matching the current search and filters"
        >
          <FontAwesomeIcon icon={faDownload} />
          {downloadingCsv
            ? 'Downloading...'
            : `Download CSV${summary ? ` (${summary.total.toLocaleString()})` : ''}`}
        </button>
        <button
          type="button"
          className="filter-toggle-button sync-status-button"
          onClick={handleSyncFiltered}
          disabled={!summary || summary.total === 0 || Boolean(syncRun?.running)}
          title="Re-check first contract year and Amway registration for every user matching the current search and filters"
        >
          <FontAwesomeIcon icon={faUserCheck} />
          {syncRun?.running
            ? 'Syncing...'
            : `Sync status${summary ? ` (${summary.total.toLocaleString()})` : ''}`}
        </button>
      </div>

      {showFilters && (
        <div className="user-filters">
          <label className="user-filter">
            <span>Team</span>
            <select value={filters.team} onChange={(e) => handleFilterChange('team', e.target.value)}>
              <option value="">Any</option>
              {teams.map(team => (
                <option key={team.id} value={team.name}>{team.name}</option>
              ))}
            </select>
          </label>
          {FILTER_DEFS.map(({ key, label, options }) => (
            <label key={key} className="user-filter">
              <span>{label}</span>
              <select value={filters[key]} onChange={(e) => handleFilterChange(key, e.target.value)}>
                <option value="">Any</option>
                {options.map(([value, text]) => (
                  <option key={value} value={value}>{text}</option>
                ))}
              </select>
            </label>
          ))}
          <label className="user-filter">
            <span>Created from</span>
            <input
              type="date"
              value={filters.createdFrom}
              max={filters.createdTo || undefined}
              onChange={(e) => handleFilterChange('createdFrom', e.target.value)}
            />
          </label>
          <label className="user-filter">
            <span>Created to</span>
            <input
              type="date"
              value={filters.createdTo}
              min={filters.createdFrom || undefined}
              onChange={(e) => handleFilterChange('createdTo', e.target.value)}
            />
          </label>
        </div>
      )}

      {summary && (
        <div className="user-summary">
          {SUMMARY_CARDS.map(({ key, label }) => (
            <div key={key} className={`user-summary-card ${key === 'total' ? 'total' : ''}`}>
              <span className="user-summary-value">{(summary[key] ?? 0).toLocaleString()}</span>
              <span className="user-summary-label">{label}</span>
            </div>
          ))}
        </div>
      )}

      {syncRun && (
        <UserStatusSyncPanel run={syncRun} onStop={handleStopSync} onClose={() => setSyncRun(null)} />
      )}

      {(activeFilterChips.length > 0 || searchTerm) && (
        <div className="active-filters">
          <span className="active-filters-label">Active filters:</span>
          {searchTerm && (
            <span className="filter-chip">
              Search: {searchTerm}
              <button type="button" onClick={() => setSearchInput('')} aria-label="Clear search">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          )}
          {activeFilterChips.map(chip => (
            <span key={chip.key} className="filter-chip">
              {chip.text}
              <button
                type="button"
                onClick={() => handleFilterChange(chip.key, '')}
                aria-label={`Remove ${chip.text}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          ))}
          <button type="button" className="clear-filters-button" onClick={handleClearFilters}>
            Clear all
          </button>
        </div>
      )}

      {loading && <div className="loading-indicator">Loading users...</div>}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {!loading && users.length === 0 && !error && (
        <div className="no-users">
          <p>No users found.</p>
          {(searchTerm || activeFilterChips.length > 0) && <p>Try adjusting your search or filters.</p>}
        </div>
      )}

      {!loading && users.length > 0 && (
        <>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('iboNumber')} className="sortable">
                    IBO Number
                    <FontAwesomeIcon icon={getSortIcon('iboNumber')} className="sort-icon" />
                  </th>
                  <th onClick={() => handleSort('email')} className="sortable">
                    Email
                    <FontAwesomeIcon icon={getSortIcon('email')} className="sort-icon" />
                  </th>
                  <th onClick={() => handleSort('iboName')} className="sortable">
                    Name
                    <FontAwesomeIcon icon={getSortIcon('iboName')} className="sort-icon" />
                  </th>
                  <th>Phone</th>
                  <th>Secondary Phone</th>
                  <th onClick={() => handleSort('teamName')} className="sortable">
                    Team
                    <FontAwesomeIcon icon={getSortIcon('teamName')} className="sort-icon" />
                  </th>
                  <th>KPI Status</th>
                  <th>Status</th>
                  <th onClick={() => handleSort('createdAt')} className="sortable">
                    Created
                    <FontAwesomeIcon icon={getSortIcon('createdAt')} className="sort-icon" />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="user-row-clickable" onClick={() => handleViewUser(user)}>
                    <td className="ibo-number">{user.iboNumber}</td>
                    <td className="email">{user.email}</td>
                    <td className="name">{user.iboName || 'N/A'}</td>
                    <td className="phone">{user.phone || 'N/A'}</td>
                    <td className="phone">{user.secondaryPhone ? `${user.secondaryPhone}${user.secondaryPhoneVerified ? ' ✓' : ' ✗'}` : 'N/A'}</td>
                    <td className="team">{user.teamName}</td>
                    <td className="kpi-status" onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const kpiStatus = getKpiStatus(user.metadata);
                        const needsRefresh = isKpiInvalid(user.metadata);
                        return (
                          <div className="kpi-status-cell">
                            <span 
                              className={`kpi-badge kpi-${kpiStatus.status}`}
                              style={{ backgroundColor: kpiStatus.color }}
                            >
                              {kpiStatus.text}
                            </span>
                            <button
                              className={`refresh-kpi-button ${needsRefresh ? 'needs-refresh' : ''}`}
                              onClick={() => handleRefreshKpi(user.iboNumber)}
                              disabled={refreshingKpi === user.iboNumber}
                              title={needsRefresh ? 'Invalid KPI - Click to refresh' : 'Refresh KPI data from Amway'}
                            >
                              <FontAwesomeIcon 
                                icon={faSync} 
                                spin={refreshingKpi === user.iboNumber}
                              />
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="status">
                      <div className="status-badges">
                        <span className={`status-badge ${user.emailVerified ? 'verified' : 'unverified'}`}>
                          Email {user.emailVerified ? 'Verified' : 'Unverified'}
                        </span>
                        <span className={`status-badge ${user.userVerified ? 'verified' : 'unverified'}`}>
                          {user.userVerified ? 'Active' : 'Inactive'}
                        </span>
                        {user.isFirstContractYear && (
                          <span className="status-badge first-year">First Year</span>
                        )}
                      </div>
                    </td>
                    <td className="created-date">{formatDate(user.createdAt)}</td>
                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="edit-user-button"
                        onClick={() => handleEditUser(user)}
                        title="Edit user"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="sync-user-button"
                        onClick={() => handleSyncUser(user.iboNumber)}
                        disabled={syncingUser === user.iboNumber}
                        title="Sync first contract year and registration with Amway"
                        aria-label={`Sync status of ${user.iboNumber}`}
                      >
                        <FontAwesomeIcon icon={faUserCheck} beat={syncingUser === user.iboNumber} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {pagination.currentPage * pagination.pageSize + 1} to{' '}
              {Math.min((pagination.currentPage + 1) * pagination.pageSize, pagination.totalElements)} of{' '}
              {pagination.totalElements} users
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevious}
                className="pagination-button"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                Previous
              </button>
              
              <span className="page-info">
                Page {pagination.currentPage + 1} of {pagination.totalPages}
              </span>
              
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="pagination-button"
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderUplineManagement = () => (
    <div className="upline-management-section">
      <div className="section-header">
        <h3>
          <FontAwesomeIcon icon={faUsers} />
          Manage Upline Platinum IBO
        </h3>
        <p className="section-description">
          Users with missing upline platinum IBO assignments
        </p>
      </div>

      {loading && <div className="loading-indicator">Loading users...</div>}
      
      {!loading && nullUplineUsers.length === 0 && !error && (
        <div className="no-users">
          <p>✅ All users have upline platinum IBO assigned!</p>
        </div>
      )}

      {!loading && nullUplineUsers.length > 0 && (
        <>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>IBO Number</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Secondary Phone</th>
                  <th>Team</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {nullUplineUsers.map(user => (
                  <tr key={user.id} className={selectedUserForUpline?.id === user.id ? 'selected-row' : ''}>
                    <td className="ibo-number">{user.iboNumber}</td>
                    <td className="name">{user.iboName || 'N/A'}</td>
                    <td className="email">{user.email}</td>
                    <td className="phone">{user.phone || 'N/A'}</td>
                    <td className="phone">{user.secondaryPhone ? `${user.secondaryPhone}${user.secondaryPhoneVerified ? ' ✓' : ' ✗'}` : 'N/A'}</td>
                    <td className="team">{user.teamName || 'N/A'}</td>
                    <td className="status">
                      <div className="status-badges">
                        <span className={`status-badge ${user.userVerified ? 'verified' : 'unverified'}`}>
                          {user.userVerified ? 'Active' : 'Inactive'}
                        </span>
                        {user.isFirstContractYear && (
                          <span className="status-badge first-year">First Year</span>
                        )}
                      </div>
                    </td>
                    <td className="created-date">{formatDate(user.createdAt)}</td>
                    <td>
                      <button
                        className="update-button"
                        onClick={() => {
                          setSelectedUserForUpline(user);
                          setUplinePlatinumIboValue('');
                          setError(null);
                        }}
                        disabled={updatingUpline}
                      >
                        {selectedUserForUpline?.id === user.id ? 'Selected' : 'Update'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {nullUplinePagination.currentPage * nullUplinePagination.pageSize + 1} to{' '}
              {Math.min((nullUplinePagination.currentPage + 1) * nullUplinePagination.pageSize, nullUplinePagination.totalElements)} of{' '}
              {nullUplinePagination.totalElements} users
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handleNullUplinePageChange(nullUplinePagination.currentPage - 1)}
                disabled={!nullUplinePagination.hasPrevious || loading}
                className="pagination-button"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                Previous
              </button>
              
              <span className="page-info">
                Page {nullUplinePagination.currentPage + 1} of {nullUplinePagination.totalPages}
              </span>
              
              <button
                onClick={() => handleNullUplinePageChange(nullUplinePagination.currentPage + 1)}
                disabled={!nullUplinePagination.hasNext || loading}
                className="pagination-button"
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Update Form */}
      {selectedUserForUpline && (
        <div className="update-upline-form">
          <div className="form-header">
            <h4>Update Upline Platinum IBO</h4>
            <button
              className="close-form-button"
              onClick={() => {
                setSelectedUserForUpline(null);
                setUplinePlatinumIboValue('');
                setError(null);
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="selected-user-info">
            <div className="info-row">
              <strong>User:</strong> {selectedUserForUpline.iboName || 'N/A'}
            </div>
            <div className="info-row">
              <strong>IBO Number:</strong> {selectedUserForUpline.iboNumber}
            </div>
            <div className="info-row">
              <strong>Email:</strong> {selectedUserForUpline.email}
            </div>
            <div className="info-row">
              <strong>Team:</strong> {selectedUserForUpline.teamName || 'N/A'}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="uplinePlatinumIbo">Upline Platinum IBO *</label>
            <input
              type="text"
              id="uplinePlatinumIbo"
              value={uplinePlatinumIboValue}
              onChange={(e) => setUplinePlatinumIboValue(e.target.value)}
              placeholder="e.g., PLAT001234"
              disabled={updatingUpline}
            />
          </div>

          <div className="form-actions">
            <button
              onClick={() => {
                setSelectedUserForUpline(null);
                setUplinePlatinumIboValue('');
                setError(null);
              }}
              disabled={updatingUpline}
              className="cancel-button"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateUplinePlatinumIbo}
              disabled={updatingUpline || !uplinePlatinumIboValue.trim()}
              className="submit-button"
            >
              {updatingUpline ? (
                <>Updating...</>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} />
                  Update Upline IBO
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderCreateUser = () => (
    <div className="create-user-section">
      <div className="section-header">
        <h3>
          <FontAwesomeIcon icon={faUserPlus} />
          Create New User
        </h3>
      </div>

      <form onSubmit={handleCreateUser} className="create-user-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={createUserForm.email}
              onChange={handleCreateUserInputChange}
              required
              placeholder="user@example.com"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="iboNumber">IBO Number *</label>
            <input
              type="text"
              id="iboNumber"
              name="iboNumber"
              value={createUserForm.iboNumber}
              onChange={handleCreateUserInputChange}
              required
              placeholder="123456789"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={createUserForm.phoneNumber}
              onChange={handleCreateUserInputChange}
              placeholder="+1234567890"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="teamName">Team *</label>
            <select
              id="teamName"
              name="teamName"
              value={createUserForm.teamName}
              onChange={handleCreateUserInputChange}
              required
              disabled={loadingTeams}
            >
              {loadingTeams ? (
                <option>Loading teams...</option>
              ) : (
                <>
                  <option value="">Select a team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="sendInEnglish"
                checked={createUserForm.sendInEnglish}
                onChange={handleCreateUserInputChange}
              />
              <span className="checkmark"></span>
              Send Registration Email in English
              <span className="language-note">(Default: Spanish)</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={creatingUser || loadingTeams}
            className="submit-button"
          >
            {creatingUser ? (
              <>Creating User...</>
            ) : (
              <>
                <FontAwesomeIcon icon={faUserPlus} />
                Create User
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="user-management-container">
      <div className="page-header">
        <button onClick={handleBack} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Dashboard
        </button>
        <h2>User Management</h2>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Section Navigation */}
      <div className="section-navigation">
        <button
          className={`section-button ${activeSection === 'view' ? 'active' : ''}`}
          onClick={() => setActiveSection('view')}
        >
          <FontAwesomeIcon icon={faUsers} />
          View Users
        </button>
        <button
          className={`section-button ${activeSection === 'create' ? 'active' : ''}`}
          onClick={() => setActiveSection('create')}
        >
          <FontAwesomeIcon icon={faUserPlus} />
          Create User
        </button>
        <button
          className={`section-button ${activeSection === 'upline' ? 'active' : ''}`}
          onClick={() => setActiveSection('upline')}
        >
          <FontAwesomeIcon icon={faUsers} />
          Upline Management
        </button>
      </div>

      {/* Section Content */}
      <div className="section-content">
        {activeSection === 'view' && renderViewUsers()}
        {activeSection === 'create' && renderCreateUser()}
        {activeSection === 'upline' && renderUplineManagement()}
      </div>

      {/* View User Details Modal */}
      {selectedUserForView && (
        <div className="view-user-modal-overlay">
          <div className="view-user-modal">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faUsers} />
                User Details
              </h3>
              <button
                className="close-modal-button"
                onClick={() => setSelectedUserForView(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              <div className="user-details-grid">
                {/* Basic Information */}
                <div className="detail-section">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faIdCard} />
                    Basic Information
                  </h4>
                  <div className="detail-item">
                    <span className="detail-label">User ID:</span>
                    <span className="detail-value">{selectedUserForView.id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">IBO Number:</span>
                    <span className="detail-value">{selectedUserForView.iboNumber}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{selectedUserForView.iboName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{selectedUserForView.email}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{selectedUserForView.phone || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Secondary Phone:</span>
                    <span className="detail-value">
                      {selectedUserForView.secondaryPhone 
                        ? `${selectedUserForView.secondaryPhone} ${selectedUserForView.secondaryPhoneVerified ? '(Verified)' : '(Not Verified)'}` 
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Team & Upline Information */}
                <div className="detail-section">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faBuilding} />
                    Team & Upline Information
                  </h4>
                  <div className="detail-item">
                    <span className="detail-label">Team Name:</span>
                    <span className="detail-value">{selectedUserForView.teamName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Upline Platinum IBO:</span>
                    <span className="detail-value">{selectedUserForView.uplinePlatinumIbo || 'N/A'}</span>
                  </div>
                </div>

                {/* Addresses */}
                <div className="detail-section">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    Addresses
                  </h4>
                  {selectedUserForView.addresses && selectedUserForView.addresses.length > 0 ? (
                    selectedUserForView.addresses.map((address, index) => (
                      <div key={address.id} className="detail-item full-width">
                        <span className="detail-label address-label">
                          {address.label || `Address ${index + 1}`}
                          {address.isDefault && (
                            <span className="status-badge verified">Default</span>
                          )}
                        </span>
                        <span className="detail-value address-value">
                          <span>{address.streetLine1}</span>
                          {address.streetLine2 && <span>{address.streetLine2}</span>}
                          <span>{[address.city, [address.state, address.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span>
                          {address.country && <span>{address.country}</span>}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="detail-item full-width">
                      <span className="detail-value">No address on file</span>
                    </div>
                  )}
                </div>

                {/* KPI Information */}
                <div className="detail-section">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faChartLine} />
                    KPI Information
                  </h4>
                  {selectedUserForView.metadata ? (
                    <>
                      {selectedUserForView.metadata.message ? (
                        <div className="detail-item full-width">
                          <span className="detail-label">Status:</span>
                          <span className="detail-value error-text">{selectedUserForView.metadata.message}</span>
                        </div>
                      ) : (
                        <>
                          <div className="detail-item">
                            <span className="detail-label">IBO Name:</span>
                            <span className="detail-value">{selectedUserForView.metadata.ibo_name || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Current Award:</span>
                            <span className="detail-value">{selectedUserForView.metadata.current_award || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Highest Award:</span>
                            <span className="detail-value">{selectedUserForView.metadata.highest_award || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Application Date:</span>
                            <span className="detail-value">{selectedUserForView.metadata.application_date || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Personal PV:</span>
                            <span className="detail-value">{selectedUserForView.metadata.personal_pv_value || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Group PV:</span>
                            <span className="detail-value">{selectedUserForView.metadata.group_pv_value || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">VCS PV:</span>
                            <span className="detail-value">{selectedUserForView.metadata.vcs_pv_value || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">VCS Percentage:</span>
                            <span className="detail-value">{selectedUserForView.metadata.vcs_percentage || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Upline Platinum:</span>
                            <span className="detail-value">{selectedUserForView.metadata.upline_platinum_ibo || 'N/A'}</span>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="detail-item full-width">
                      <span className="detail-value">No KPI data available</span>
                    </div>
                  )}
                </div>

                {/* Verification Status */}
                <div className="detail-section">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    Verification Status
                  </h4>
                  <div className="detail-item">
                    <span className="detail-label">Email Verified:</span>
                    <span className={`detail-value ${selectedUserForView.emailVerified ? 'success-text' : 'error-text'}`}>
                      <FontAwesomeIcon icon={selectedUserForView.emailVerified ? faCheck : faTimes} />
                      {selectedUserForView.emailVerified ? ' Verified' : ' Unverified'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Phone Verified:</span>
                    <span className={`detail-value ${selectedUserForView.phoneVerified ? 'success-text' : 'error-text'}`}>
                      <FontAwesomeIcon icon={selectedUserForView.phoneVerified ? faCheck : faTimes} />
                      {selectedUserForView.phoneVerified ? ' Verified' : ' Unverified'}
                    </span>
                  </div>
                  {selectedUserForView.secondaryPhone && (
                    <div className="detail-item">
                      <span className="detail-label">Secondary Phone Verified:</span>
                      <span className={`detail-value ${selectedUserForView.secondaryPhoneVerified ? 'success-text' : 'error-text'}`}>
                        <FontAwesomeIcon icon={selectedUserForView.secondaryPhoneVerified ? faCheck : faTimes} />
                        {selectedUserForView.secondaryPhoneVerified ? ' Verified' : ' Unverified'}
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">User Status:</span>
                    <span className={`detail-value ${selectedUserForView.userVerified ? 'success-text' : 'error-text'}`}>
                      <FontAwesomeIcon icon={selectedUserForView.userVerified ? faCheck : faTimes} />
                      {selectedUserForView.userVerified ? ' Active' : ' Inactive'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">First Contract Year:</span>
                    <span className={`detail-value ${selectedUserForView.isFirstContractYear ? 'info-text' : ''}`}>
                      <FontAwesomeIcon icon={selectedUserForView.isFirstContractYear ? faCheck : faTimes} />
                      {selectedUserForView.isFirstContractYear ? ' Yes' : ' No'}
                    </span>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="detail-section">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faClock} />
                    Account Information
                  </h4>
                  <div className="detail-item">
                    <span className="detail-label">Created At:</span>
                    <span className="detail-value">{formatDate(selectedUserForView.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Updated At:</span>
                    <span className="detail-value">{formatDate(selectedUserForView.updatedAt)}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="modal-actions">
                <button
                  className="refresh-kpi-modal-button"
                  onClick={() => handleRefreshKpi(selectedUserForView.iboNumber)}
                  disabled={refreshingKpi === selectedUserForView.iboNumber}
                  title="Refresh KPI data from Amway"
                >
                  <FontAwesomeIcon 
                    icon={faSync} 
                    spin={refreshingKpi === selectedUserForView.iboNumber}
                  />
                  {refreshingKpi === selectedUserForView.iboNumber ? ' Refreshing...' : ' Refresh KPI'}
                </button>
                <button
                  className="refresh-kpi-modal-button"
                  onClick={() => handleSyncUser(selectedUserForView.iboNumber)}
                  disabled={syncingUser === selectedUserForView.iboNumber}
                  title="Sync first contract year and registration with Amway"
                >
                  <FontAwesomeIcon icon={faUserCheck} />
                  {syncingUser === selectedUserForView.iboNumber ? ' Syncing...' : ' Sync Status'}
                </button>
                <button
                  className="edit-from-view-button"
                  onClick={() => handleEditUser(selectedUserForView)}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  Edit User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {selectedUserForEdit && (
        <div className="edit-user-modal-overlay">
          <div className="edit-user-modal">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faEdit} />
                Edit User
              </h3>
              <button
                className="close-modal-button"
                onClick={() => {
                  setSelectedUserForEdit(null);
                  setEditModalError(null);
                }}
                disabled={updatingUser}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              {/* Error Message within Modal */}
              {editModalError && (
                <div className="modal-error-message">
                  <FontAwesomeIcon icon={faTimes} />
                  {editModalError}
                </div>
              )}
              <div className="user-info-banner">
                <div className="info-item">
                  <strong>IBO Number:</strong> {selectedUserForEdit.iboNumber}
                </div>
                <div className="info-item">
                  <strong>User ID:</strong> {selectedUserForEdit.id}
                </div>
              </div>

              <form onSubmit={handleUpdateUser} className="edit-user-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-email">Email Address</label>
                    <input
                      type="email"
                      id="edit-email"
                      name="email"
                      value={editUserForm.email}
                      onChange={handleEditUserFormChange}
                      disabled={updatingUser}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-phone">Phone Number</label>
                    <input
                      type="tel"
                      id="edit-phone"
                      name="phone"
                      value={editUserForm.phone}
                      onChange={handleEditUserFormChange}
                      disabled={updatingUser}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-iboName">IBO Name</label>
                    <input
                      type="text"
                      id="edit-iboName"
                      name="iboName"
                      value={editUserForm.iboName}
                      onChange={handleEditUserFormChange}
                      disabled={updatingUser}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-uplinePlatinumIbo">Upline Platinum IBO</label>
                    <input
                      type="text"
                      id="edit-uplinePlatinumIbo"
                      name="uplinePlatinumIbo"
                      value={editUserForm.uplinePlatinumIbo}
                      onChange={handleEditUserFormChange}
                      disabled={updatingUser}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-teamName">Team</label>
                  <select
                    id="edit-teamName"
                    name="teamName"
                    value={editUserForm.teamName}
                    onChange={handleEditUserFormChange}
                    disabled={updatingUser || loadingTeams}
                  >
                    {loadingTeams ? (
                      <option>Loading teams...</option>
                    ) : (
                      <>
                        <option value="">Select a team</option>
                        {teams.map(team => (
                          <option key={team.id} value={team.name}>
                            {team.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                {renderAddressEditor()}

                <div className="form-section-title">Verification Status</div>
                
                <div className="checkbox-group-row">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="emailVerified"
                        checked={editUserForm.emailVerified}
                        onChange={handleEditUserFormChange}
                        disabled={updatingUser}
                      />
                      <span className="checkmark"></span>
                      Email Verified
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="phoneVerified"
                        checked={editUserForm.phoneVerified}
                        onChange={handleEditUserFormChange}
                        disabled={updatingUser}
                      />
                      <span className="checkmark"></span>
                      Phone Verified
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="userVerified"
                        checked={editUserForm.userVerified}
                        onChange={handleEditUserFormChange}
                        disabled={updatingUser}
                      />
                      <span className="checkmark"></span>
                      User Verified (Active)
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isFirstContractYear"
                        checked={editUserForm.isFirstContractYear}
                        onChange={handleEditUserFormChange}
                        disabled={updatingUser}
                      />
                      <span className="checkmark"></span>
                      First Contract Year
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUserForEdit(null);
                      setEditModalError(null);
                    }}
                    disabled={updatingUser}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updatingUser}
                    className="submit-button"
                  >
                    {updatingUser ? (
                      <>Updating...</>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheck} />
                        Update User
                      </>
                    )}
                  </button>
                </div>
              </form>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage; 